import { OriginalElementState, DOMProvider, DefaultDOMProvider } from './ModalInterfaces';
import { EXCLUDED_ELEMENT_TAGS, EXCLUDED_ELEMENT_CLASSES } from './ModalConstants';

/**
 * ModalAccessibilityManager - Handles background content isolation using aria-hidden and inert attributes
 */
export class ModalAccessibilityManager {
    private _modalElement: Element;
    private _domProvider: DOMProvider;
    private _isolatedElements: Map<Element, OriginalElementState>;
    private _isIsolationActive: boolean = false;

    constructor(modalElement: Element, domProvider?: DOMProvider) {
        this._modalElement = modalElement;
        this._domProvider = domProvider || new DefaultDOMProvider();
        this._isolatedElements = new Map<Element, OriginalElementState>();
    }

    /**
     * Isolate background content by applying aria-hidden and inert
     * to all direct children of document.body except the modal
     * This works properly now that modal is moved to body level
     */
    public isolateBackgroundContent(): void {
        if (this._isIsolationActive) {
            return; // Already isolated
        }

        // Clear any existing isolation state
        this.restoreBackgroundContent();

        // Get all direct children of document.body
        const bodyChildren: Element[] = Array.from(this._domProvider.getBody().children);

        bodyChildren.forEach((element: Element) => {
            // Skip the modal element itself
            if (element === this._modalElement) {
                return;
            }

            // Skip scripts (they don't contain content that screen readers navigate)
            if (EXCLUDED_ELEMENT_TAGS.includes(element.tagName.toLowerCase())) {
                return;
            }

            // Skip modal backdrops (Bootstrap creates these dynamically)
            if (EXCLUDED_ELEMENT_CLASSES.some(className => element.classList.contains(className))) {
                return;
            }

            // Store original state for restoration
            const originalState: OriginalElementState = {
                ariaHidden: element.getAttribute('aria-hidden'),
                inert: element.hasAttribute('inert')
            };

            this._isolatedElements.set(element, originalState);

            // Apply isolation attributes
            element.setAttribute('aria-hidden', 'true');
            element.setAttribute('inert', '');
        });

        this._isIsolationActive = true;
        console.log(`Background isolation applied to ${this._isolatedElements.size} elements`);
    }

    /**
     * Restore background content by removing isolation attributes
     * and restoring original values
     */
    public restoreBackgroundContent(): void {
        if (!this._isIsolationActive) {
            return; // Nothing to restore
        }

        this._isolatedElements.forEach((originalState: OriginalElementState, element: Element) => {
            // Restore aria-hidden to original state
            if (originalState.ariaHidden !== null) {
                element.setAttribute('aria-hidden', originalState.ariaHidden);
            } else {
                element.removeAttribute('aria-hidden');
            }

            // Restore inert to original state
            if (!originalState.inert) {
                element.removeAttribute('inert');
            }
        });

        console.log(`Background isolation removed from ${this._isolatedElements.size} elements`);
        // Clear the tracking map
        this._isolatedElements.clear();
        this._isIsolationActive = false;
    }

    /**
     * Check if background isolation is currently active
     */
    public isIsolationActive(): boolean {
        return this._isIsolationActive;
    }

    /**
     * Clean up and dispose of the manager
     */
    public dispose(): void {
        this.restoreBackgroundContent();
        this._isolatedElements.clear();
        this._isIsolationActive = false;
    }
}